import React, { useState, useEffect } from 'react';
import { requirementAPI } from '../services/api';

const STATUS_COLORS = {
  pending_review: 'var(--yellow)',
  open: 'var(--accent)',
  matched: 'var(--green)',
  closed: 'var(--text3)',
  rejected: 'var(--red)',
  interested: 'var(--yellow)',
  approved: 'var(--green)',
  declined: 'var(--red)',
};

const CATEGORY_OPTIONS = [
  { value: 'installation', label: 'Installation' },
  { value: 'product_supply', label: 'Product Supply' },
  { value: 'amc', label: 'AMC' },
  { value: 'repair', label: 'Repair' },
  { value: 'other', label: 'Other' },
];

const URGENCY_OPTIONS = [
  { value: 'immediate', label: 'Immediate' },
  { value: 'within_week', label: 'Within a Week' },
  { value: 'within_month', label: 'Within a Month' },
  { value: 'flexible', label: 'Flexible' },
];

const urgencyLabel = (value) =>
  URGENCY_OPTIONS.find(u => u.value === value)?.label || value;

const Badge = ({ status, label }) => (
  <span style={{
    background: `${STATUS_COLORS[status] || 'var(--text3)'}22`,
    color: STATUS_COLORS[status] || 'var(--text3)',
    padding: '3px 10px',
    borderRadius: '20px',
    fontSize: '11px',
    fontFamily: 'DM Mono,monospace',
    fontWeight: '600',
    textTransform: 'capitalize',
    whiteSpace: 'nowrap',
  }}>
    {label || (status || '').replace(/_/g, ' ')}
  </span>
);

const inputStyle = {
  width: '100%',
  background: 'var(--surface2)',
  border: '1px solid var(--border2)',
  color: 'var(--text)',
  padding: '11px 14px',
  borderRadius: '8px',
  fontSize: '14px',
  outline: 'none',
};

const labelStyle = {
  display: 'block',
  fontSize: '11px',
  fontFamily: 'DM Mono,monospace',
  color: 'var(--text3)',
  letterSpacing: '1px',
  textTransform: 'uppercase',
  marginBottom: '6px',
};

const categoryLabel = (value) =>
  CATEGORY_OPTIONS.find(c => c.value === value)?.label || value;

const EMPTY_FORM = {
  category: '',
  title: '',
  description: '',
  city: '',
  address: '',
  quantity: '',
  urgency: '',
  contact_name: '',
  contact_phone: '',
  contact_email: '',
};

const VendorRequirements = () => {
  const [tab, setTab] = useState('board'); // 'board' | 'mine'
  const [board, setBoard] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [attachment, setAttachment] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (tab === 'board') loadBoard();
    else loadMine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const loadBoard = async () => {
    setLoading(true);
    try {
      const res = await requirementAPI.getAll({});
      setBoard(res.data.data);
    } catch (err) {
      console.error('Failed to load open board:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMine = async () => {
    setLoading(true);
    try {
      const res = await requirementAPI.getAll({ view: 'mine' });
      setMine(res.data.data);
    } catch (err) {
      console.error('Failed to load submissions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (id) => {
    const message = prompt('Add a short note for the admin (optional):') || '';
    setActionLoading(id + '_respond');
    try {
      await requirementAPI.respond(id, { message });
      await loadBoard();
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to submit interest');
    } finally {
      setActionLoading('');
    }
  };

  const update = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setAttachment(null);
    setFormError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!form.category || !form.title || !form.description) {
      setFormError('Category, title, and description are required');
      return;
    }

    setFormLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value !== '' && value != null) fd.append(key, value);
      });
      if (attachment) fd.append('attachment', attachment);

      await requirementAPI.create(fd);
      setShowForm(false);
      resetForm();
      setTab('mine');
    } catch (err) {
      setFormError(err.response?.data?.message || 'Failed to submit requirement');
    } finally {
      setFormLoading(false);
    }
  };

  const cardBase = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '18px 20px',
    marginBottom: '12px',
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
      }}>
        <div>
          <h1 style={{
            fontFamily: 'Syne,sans-serif',
            fontSize: '22px', fontWeight: '800',
          }}>
            Requirements
          </h1>
          <p style={{
            color: 'var(--text3)',
            fontSize: '13px', marginTop: '4px',
          }}>
            Browse open requirements or submit your own to the network
          </p>
        </div>
        <button
          onClick={() => { resetForm(); setShowForm(true); }}
          style={{
            background: 'var(--accent)',
            color: 'var(--bg)',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          + Submit Requirement
        </button>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        background: 'var(--surface2)',
        borderRadius: '10px',
        padding: '4px',
        marginBottom: '20px',
        maxWidth: 'fit-content',
      }}>
        {[
          { key: 'board', label: 'Open Board' },
          { key: 'mine', label: 'My Submissions' },
        ].map(t => (
          <button key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              padding: '8px 18px',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              transition: 'all 0.15s',
              background: tab === t.key ? 'var(--accent)' : 'transparent',
              color: tab === t.key ? 'var(--bg)' : 'var(--text2)',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: 'var(--text3)',
          fontFamily: 'DM Mono,monospace',
          fontSize: '13px',
        }}>
          Loading...
        </div>
      ) : tab === 'board' ? (
        board.length === 0 ? (
          <div style={{ ...cardBase, textAlign: 'center', color: 'var(--text3)', padding: '40px' }}>
            No open requirements right now
          </div>
        ) : (
          board.map(r => (
            <div key={r.id} style={cardBase}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '8px',
              }}>
                <div style={{ fontWeight: '700', fontSize: '15px' }}>{r.title}</div>
                {r.my_response_status ? (
                  <Badge status={r.my_response_status} />
                ) : (
                  <Badge status="open" label="Open" />
                )}
              </div>

              <div style={{
                display: 'flex', gap: '14px', flexWrap: 'wrap',
                fontSize: '12px', color: 'var(--text3)',
                fontFamily: 'DM Mono,monospace',
                marginBottom: '10px',
              }}>
                <span>{categoryLabel(r.category)}</span>
                {r.city && <span>{r.city}</span>}
                {r.quantity && <span>Qty: {r.quantity}</span>}
                {r.urgency && <span>Urgency: {urgencyLabel(r.urgency)}</span>}
                <span>{new Date(r.created_at).toLocaleDateString('en-IN')}</span>
              </div>

              <div style={{
                fontSize: '13.5px',
                color: 'var(--text2)',
                lineHeight: 1.5,
                marginBottom: '12px',
              }}>
                {r.description}
              </div>

              {r.my_response_status === 'approved' && (
                <div style={{
                  background: 'var(--surface2)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  padding: '12px 14px',
                  marginBottom: '12px',
                  fontSize: '13px',
                }}>
                  <div style={{ color: 'var(--text3)', fontSize: '11px', fontFamily: 'DM Mono,monospace', marginBottom: '6px' }}>
                    CONTACT DETAILS
                  </div>
                  <div><strong style={{ color: 'var(--text)' }}>{r.contact_name || '—'}</strong></div>
                  <div style={{ color: 'var(--text2)' }}>{r.contact_phone || '—'}</div>
                  <div style={{ color: 'var(--text2)' }}>{r.contact_email || '—'}</div>
                </div>
              )}

              {!r.my_response_status && (
                <button
                  onClick={() => handleRespond(r.id)}
                  disabled={actionLoading === r.id + '_respond'}
                  style={{
                    background: 'rgba(0,212,255,0.12)',
                    color: 'var(--accent)',
                    border: '1px solid rgba(0,212,255,0.3)',
                    padding: '7px 16px',
                    borderRadius: '6px',
                    fontSize: '12.5px',
                    fontWeight: '600',
                    cursor: 'pointer',
                  }}
                >
                  {actionLoading === r.id + '_respond' ? 'Submitting...' : 'Express Interest'}
                </button>
              )}
            </div>
          ))
        )
      ) : (
        mine.length === 0 ? (
          <div style={{ ...cardBase, textAlign: 'center', color: 'var(--text3)', padding: '40px' }}>
            You haven't submitted any requirements yet
          </div>
        ) : (
          mine.map(r => (
            <div key={r.id} style={cardBase}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '8px',
              }}>
                <div style={{ fontWeight: '700', fontSize: '15px' }}>{r.title}</div>
                <Badge status={r.status} />
              </div>

              <div style={{
                display: 'flex', gap: '14px', flexWrap: 'wrap',
                fontSize: '12px', color: 'var(--text3)',
                fontFamily: 'DM Mono,monospace',
                marginBottom: '10px',
              }}>
                <span>{categoryLabel(r.category)}</span>
                {r.city && <span>{r.city}</span>}
                <span>{r.response_count != null ? r.response_count : 0} response{r.response_count === 1 ? '' : 's'}</span>
                <span>{new Date(r.created_at).toLocaleDateString('en-IN')}</span>
              </div>

              <div style={{
                fontSize: '13.5px',
                color: 'var(--text2)',
                lineHeight: 1.5,
              }}>
                {r.description}
              </div>

              {r.admin_notes && (
                <div style={{
                  marginTop: '10px',
                  fontSize: '12.5px',
                  color: 'var(--text3)',
                  borderTop: '1px solid var(--border)',
                  paddingTop: '10px',
                }}>
                  Admin note: {r.admin_notes}
                </div>
              )}
            </div>
          ))
        )
      )}

      {/* Submit form modal */}
      {showForm && (
        <div
          onClick={() => setShowForm(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            zIndex: 100,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%',
              maxWidth: '560px',
              maxHeight: '90vh',
              overflowY: 'auto',
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: '16px',
              padding: '28px',
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
            }}>
              <h2 style={{
                fontFamily: 'Syne,sans-serif',
                fontSize: '18px', fontWeight: '800',
              }}>
                Submit Requirement
              </h2>
              <button
                onClick={() => setShowForm(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text3)',
                  fontSize: '20px',
                  cursor: 'pointer',
                  lineHeight: 1,
                }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px',
                marginBottom: '16px',
              }}>
                <div>
                  <label style={labelStyle}>Category *</label>
                  <select style={inputStyle}
                    value={form.category}
                    onChange={e => update('category', e.target.value)}
                  >
                    <option value="">— Select —</option>
                    {CATEGORY_OPTIONS.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Urgency</label>
                  <select style={inputStyle}
                    value={form.urgency}
                    onChange={e => update('urgency', e.target.value)}
                  >
                    <option value="">— Select —</option>
                    {URGENCY_OPTIONS.map(u => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Title *</label>
                <input style={inputStyle}
                  value={form.title}
                  onChange={e => update('title', e.target.value)}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Description *</label>
                <textarea
                  style={{ ...inputStyle, minHeight: '90px', resize: 'vertical', fontFamily: 'inherit' }}
                  value={form.description}
                  onChange={e => update('description', e.target.value)}
                />
              </div>

              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '16px',
                marginBottom: '16px',
              }}>
                <div>
                  <label style={labelStyle}>City</label>
                  <input style={inputStyle}
                    value={form.city}
                    onChange={e => update('city', e.target.value)}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Quantity</label>
                  <input type="number" min="1" style={inputStyle}
                    value={form.quantity}
                    onChange={e => update('quantity', e.target.value)}
                  />
                </div>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Address</label>
                <input style={inputStyle}
                  value={form.address}
                  onChange={e => update('address', e.target.value)}
                />
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>Attachment (optional)</label>
                <input type="file" style={inputStyle}
                  onChange={e => setAttachment(e.target.files[0] || null)}
                />
              </div>

              <div style={{
                borderTop: '1px solid var(--border)',
                paddingTop: '16px',
                marginBottom: '16px',
              }}>
                <div style={{ ...labelStyle, marginBottom: '10px' }}>
                  Contact override (optional — defaults to your account details)
                </div>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '16px',
                  marginBottom: '16px',
                }}>
                  <div>
                    <label style={labelStyle}>Contact Name</label>
                    <input style={inputStyle}
                      value={form.contact_name}
                      onChange={e => update('contact_name', e.target.value)}
                    />
                  </div>
                  <div>
                    <label style={labelStyle}>Contact Phone</label>
                    <input style={inputStyle}
                      value={form.contact_phone}
                      onChange={e => update('contact_phone', e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Contact Email</label>
                  <input type="email" style={inputStyle}
                    value={form.contact_email}
                    onChange={e => update('contact_email', e.target.value)}
                  />
                </div>
              </div>

              {formError && (
                <div style={{
                  background: 'rgba(255,77,109,0.1)',
                  border: '1px solid rgba(255,77,109,0.3)',
                  color: 'var(--red)',
                  padding: '12px 14px',
                  borderRadius: '8px',
                  fontSize: '13px',
                  marginBottom: '16px',
                }}>
                  {formError}
                </div>
              )}

              <button type="submit" disabled={formLoading}
                style={{
                  width: '100%',
                  background: formLoading ? 'var(--surface3)' : 'var(--accent)',
                  color: formLoading ? 'var(--text3)' : 'var(--bg)',
                  border: 'none',
                  padding: '12px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '700',
                  cursor: formLoading ? 'not-allowed' : 'pointer',
                }}
              >
                {formLoading ? 'Submitting...' : 'Submit for Review →'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorRequirements;
